import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { detailCta } from './detail-cta';
import { ReadMoreText } from './ReadMoreText';
import { CONTENT_CAP_PADDED } from './large-screen';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';

export interface AboutProfessionalProps {
    /** Parenthesised eyebrow, e.g. "(About the Supplier)". */
    eyebrow?: string;
    /** Professional / brand display name (the giant heading). */
    name: string;
    /** Link to the professional's profile. */
    profileLink: string;
    /** Logo badge (rendered via the standard ProfessionalTileLogo avatar). */
    logo?: { src: string; background?: string | null };
    categories: CategoryPill[];
    categoriesLabel?: string;
    /** Plain-text summary (already HTML-stripped). */
    summary?: string;
    ctaLabel?: string;
    /** Outlined secondary action beside the profile CTA (e.g. Enquire). */
    secondaryAction?: { label: string; onClick: () => void };
    /** `light` = ink on white (product). `dark` = light type on a dark section. */
    theme?: 'light' | 'dark';
    /**
     * Drop the section gutter, hairline and eyebrow — used when the block is
     * embedded in an accordion row that provides its own chrome.
     */
    hideHeader?: boolean;
    /**
     * Hide the logo badge at the desktop breakpoint (keeping it on mobile).
     * The project detail page already shows the professional logo in its hero,
     * so this avoids duplicating it. The product "About the Supplier" layout has
     * no hero logo, so it leaves this off and keeps the badge on desktop.
     */
    hideLogoOnDesktop?: boolean;
    /**
     * `editorial` (default) is the standalone section: a 200px brand name with
     * category pills beside a large summary. `summary` is the accordion row —
     * the name at the product title's scale, no pills, and the description on
     * the left with the actions right, matching the product's own summary row
     * so the accordion reads as part of the page rather than its own poster.
     */
    layout?: 'editorial' | 'summary';
}

/**
 * Shared "About the professional" editorial block (product & project pages):
 * hairline + eyebrow, a large brand name with the logo avatar to the right, then
 * a column of category pills beside the clamped summary and a profile CTA.
 * Presentational — the page maps its loader data into the props.
 */
export const AboutProfessional = ({
    eyebrow = '(About the Supplier)',
    name,
    profileLink,
    logo,
    categories,
    categoriesLabel = 'Popular categories:',
    summary,
    ctaLabel = 'View Profile',
    secondaryAction,
    theme = 'light',
    hideHeader = false,
    hideLogoOnDesktop = false,
    layout = 'editorial',
}: AboutProfessionalProps) => {
    const isDark = theme === 'dark';
    const isSummary = layout === 'summary';

    const actions = (
        <div className="flex flex-wrap items-center gap-2">
            <RoccoLink
                to={profileLink}
                className={detailCta(
                    isDark ? 'filled-dark' : 'filled-light',
                    'w-fit',
                )}
            >
                {ctaLabel}
            </RoccoLink>
            {secondaryAction ? (
                <button
                    type="button"
                    onClick={secondaryAction.onClick}
                    className={detailCta(
                        isDark ? 'outlined-dark' : 'outlined-light',
                        'w-fit',
                    )}
                >
                    {secondaryAction.label}
                </button>
            ) : null}
        </div>
    );
    return (
        <section
            className={cn(
                hideHeader
                    ? ''
                    : 'max-md:px-6 md:px-[3rem] md:pt-[3.75rem] md:pb-[10.4rem]',
                isDark ? 'bg-transparent text-white' : 'bg-white',
            )}
        >
            <div className={CONTENT_CAP_PADDED}>
                {hideHeader ? null : (
                    <div
                        className={cn(
                            'h-px w-full',
                            isDark ? 'bg-white/20' : 'bg-black/20',
                        )}
                    />
                )}
                <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0">
                        {hideHeader ? null : (
                            <SectionLabel
                                className={cn(
                                    // Detail-page section dividers are 22px
                                    // (1.375rem @1920) with no tracking — not the
                                    // 28px hero brand eyebrow (Figma e.g. 2354:58943).
                                    // Mobile snaps to the shared 14px eyebrow token.
                                    'mt-[1.062rem] block tracking-normal max-md:text-[0.875rem]',
                                    isDark && 'text-white',
                                )}
                            >
                                {eyebrow}
                            </SectionLabel>
                        )}

                        <h2
                            className={cn(
                                // Figma "Cult Design" heading (2354:59203): 200px
                                // (12.5rem), 55 Roman, tracking -8.4px = -0.042em.
                                // Mobile drops to the restrained 32px section-heading
                                // token so the brand name doesn't dominate the page.
                                'm-0 font-sans font-normal max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em]',
                                // Summary takes the product title's scale
                                // (DetailHero compact); editorial keeps the
                                // 200px brand statement.
                                isSummary
                                    ? 'md:text-[3.9rem] md:leading-[1.08] md:tracking-[-0.125rem]'
                                    : 'md:text-[12.5rem] md:leading-[0.9] md:tracking-[-0.042em]',
                                // Name sits in an airy top gap (Figma name pt-90)
                                // so the top-aligned logo badge sits above it. The
                                // standalone version uses the eyebrow for that offset.
                                hideHeader
                                    ? 'md:mt-[4.5rem]'
                                    : 'md:mt-[5.75rem]',
                                isDark ? 'text-white' : 'text-[#1f1f21]',
                            )}
                        >
                            {name}
                        </h2>
                    </div>
                    {logo ? (
                        <ProfessionalTileLogo
                            image={{ src: logo.src, alt: `${name} logo` }}
                            background={logo.background ?? null}
                            // ~164px badge per Figma (node 2354:59205); a white logo
                            // on the white section gets a hairline border instead of
                            // blending in (contrastBorder). `hideLogoOnDesktop` drops
                            // it at md+ on the project page, whose hero already shows
                            // the logo.
                            className={cn(
                                'size-16 shrink-0 md:mt-[1.8rem] md:size-[10.2rem]',
                                hideLogoOnDesktop && 'md:hidden',
                            )}
                            contrastBorder
                        />
                    ) : null}
                </div>

                <div
                    className={cn(
                        'flex max-md:mt-8 max-md:flex-col max-md:gap-6',
                        isSummary
                            ? 'md:mt-[2.375rem] md:justify-between md:gap-[7.2rem]'
                            : 'md:mt-[4.5rem] md:gap-[28.5rem]',
                    )}
                >
                    {/* Pills are held back on the summary layout — the row
                        leads with the description there, and the pills read as
                        a second navigation beside it. */}
                    {categories.length > 0 && !isSummary ? (
                        <CategoryPills
                            label={categoriesLabel}
                            items={categories}
                            className="max-md:w-full md:w-[29.16rem]"
                        />
                    ) : null}
                    <div
                        className={cn(
                            'flex min-w-0 flex-col max-md:gap-5',
                            isSummary
                                ? 'md:max-w-[60.375rem] md:flex-1 md:gap-6'
                                : 'md:w-[52.752rem] md:gap-[4.1875rem]',
                        )}
                    >
                        {summary ? (
                            isSummary ? (
                                // Same treatment as the product description:
                                // cropped to a whole sentence near three lines
                                // with Read more continuing it inline. Here the
                                // whole paragraph links to the profile rather
                                // than to an anchor further down the page.
                                <ReadMoreText
                                    text={summary}
                                    lines={3}
                                    anchorHref={profileLink}
                                    className={cn(
                                        'm-0 font-sans leading-[1.25] font-medium tracking-[-0.025em] max-md:text-base md:text-[2.0625rem]',
                                        isDark
                                            ? 'text-[#d4d4d4]'
                                            : 'text-[#181818]',
                                    )}
                                    toggleClassName={
                                        isDark
                                            ? 'text-[#a3a3a3] hover:text-[#d4d4d4]'
                                            : 'text-[#8f8f8f] hover:text-[#181818]'
                                    }
                                />
                            ) : (
                                <p
                                    className={cn(
                                        // Figma about description (2354:59219): 39px, 1.3.
                                        'm-0 line-clamp-6 font-sans leading-[1.3] font-normal max-md:text-base md:text-[2.4375rem]',
                                        isDark
                                            ? 'text-[#d4d4d4]'
                                            : 'text-[#1f1f21]',
                                    )}
                                >
                                    {summary}
                                </p>
                            )
                        ) : null}
                        {isSummary ? null : actions}
                    </div>
                    {/* Summary puts the actions in the page's usual CTA
                        position — right-aligned, opposite the description —
                        rather than stacked beneath it. */}
                    {isSummary ? (
                        <div className="flex shrink-0 md:justify-end">
                            {actions}
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
};
